// src/hooks/useMatchEndpoint.ts
import { useCallback, useEffect, useRef, useState } from "react";
import { getRequest } from "../helpers/api.helper";
import { MatchListEntry } from "../types";

type UseMatchEndpointOptions = {
  transform?: (payload: unknown) => MatchListEntry[];
  immediate?: boolean;
  pageSize?: number;
};

type UseMatchEndpointResult = {
  matches: MatchListEntry[];
  setMatches: (matches: MatchListEntry[]) => void;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  total: number;
  hasMore: boolean;
  refetch: () => Promise<void>;
  loadMore: () => Promise<void>;
  counters: MatchCounters | null;
};

export type MatchCounters = {
  [key: string]:
    | number
    | Record<string, number | undefined>
    | MatchCounters
    | undefined;
};

const candidateListKeys = [
  "profiles",
  "items",
  "list",
  "results",
  "data",
  "matches",
  "users",
  "records",
  "rows",
];

const candidateProfileKeys = [
  "profile",
  "user",
  "targetUser",
  "shortlistedUser",
  "shortlistedUserId",
  "matchedProfile",
  "match",
  "receiver",
  "sender",
  "member",
  "profileDetails",
  "person",
  "candidate",
];

const FALLBACK_NAME = "Profile";

const toPlainObject = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : {};

const firstNonEmptyString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === "string" && value.trim().length) {
      return value.trim();
    }
    if (typeof value === "number" && Number.isFinite(value)) {
      return String(value);
    }
  }
  return undefined;
};

const firstNumber = (...values: unknown[]): number | undefined => {
  for (const value of values) {
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === "string") {
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }
  }
  return undefined;
};

const normalizePaymentStatus = (value: unknown): string => {
  if (value === undefined || value === null) return "unassigned";
  const normalized = String(value).trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (!normalized) return "unassigned";
  if (normalized === "freeprofile" || normalized === "free_profile") {
    return "free";
  }
  if (
    normalized === "unassigned" ||
    normalized === "free" ||
    normalized === "paid" ||
    normalized === "redzone"
  ) {
    return normalized;
  }
  return "unassigned";
};

const normalizeFollowUpStatus = (value: unknown): string => {
  if (value === undefined || value === null) return "none";
  const normalized = String(value).trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (!normalized) return "none";
  if (normalized === "notresponding") return "not_responding";
  if (
    normalized === "none" ||
    normalized === "callback" ||
    normalized === "not_responding"
  ) {
    return normalized;
  }
  return "none";
};

const calculateAge = (dateInput: unknown): number | undefined => {
  if (typeof dateInput !== "string" || !dateInput.trim()) {
    return undefined;
  }
  const parsed = new Date(dateInput);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }
  const today = new Date();
  let age = today.getFullYear() - parsed.getFullYear();
  const monthDiff = today.getMonth() - parsed.getMonth();
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < parsed.getDate())
  ) {
    age -= 1;
  }
  return age >= 0 ? age : undefined;
};

const extractList = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (!payload || typeof payload !== "object") {
    return [];
  }

  const visited = new Set<unknown>();
  const queue: unknown[] = [payload];

  while (queue.length) {
    const current = queue.shift();
    if (!current || typeof current !== "object" || visited.has(current)) {
      continue;
    }

    visited.add(current);
    const currentObj = current as Record<string, unknown>;

    for (const key of candidateListKeys) {
      const value = currentObj[key];
      if (Array.isArray(value)) {
        return value;
      }
    }

    for (const value of Object.values(currentObj)) {
      if (Array.isArray(value)) {
        return value;
      }
      if (value && typeof value === "object") {
        queue.push(value);
      }
    }
  }

  return [];
};

const extractProfile = (
  entry: Record<string, unknown>,
): Record<string, unknown> => {
  for (const key of candidateProfileKeys) {
    const value = entry[key];
    if (value && typeof value === "object") {
      return value as Record<string, unknown>;
    }
  }
  return entry;
};

export const defaultTransform = (payload: unknown): MatchListEntry[] => {
  const list = extractList(payload);

  return list.map((item) => {
    const entry = toPlainObject(item);
    const profile = extractProfile(entry);
    const age = calculateAge(profile.dateOfBirth);
    const height = firstNonEmptyString(profile.height);
    const locationParts = [
      firstNonEmptyString(profile.district),
      firstNonEmptyString(profile.state),
      firstNonEmptyString(profile.country),
    ].filter(Boolean) as string[];
    const location =
      locationParts.length > 0
        ? Array.from(new Set(locationParts)).join(", ")
        : undefined;

    return {
      id:
        firstNonEmptyString(profile.id, profile._id, entry.id, entry._id) ?? "",
      recordId: firstNonEmptyString(profile._id, entry._id, profile.id, entry.id),
      maritalId: profile.maritalId as string,
      fullName:
        firstNonEmptyString(profile.fullName, entry.fullName) ?? FALLBACK_NAME,
      image: firstNonEmptyString(profile.image, entry.image),
      imagePrivacy: firstNonEmptyString(
        profile.imagePrivacy,
        entry.imagePrivacy,
      ),
      gender: profile.gender as string,
      age: age?.toString(),
      height: height,
      location,
      occupation: firstNonEmptyString(profile.occupation),
      education: firstNonEmptyString(profile.education),
      profileScore: firstNumber(profile.completeness),
      views: firstNumber(profile.views),
      lastSeenAt: profile.lastSeenAt as string,
      // ✅ Add these default boolean flags to satisfy MatchListEntry
      isShortlisted: (profile.isShortlisted as boolean) || false,
      createdBy: profile.createdBy as string,
      caste: profile.caste as string,
      annualIncome: profile.annualIncome as string,
      paymentStatus: normalizePaymentStatus(
        firstNonEmptyString(
          profile.paymentStatus,
          entry.paymentStatus,
          profile.planType,
          entry.planType,
          profile.accountType,
          entry.accountType,
          profile.subscriptionType,
          entry.subscriptionType,
        ),
      ),
      password: profile.password as string,
      dateOfBirth: profile.dateOfBirth as string,
      maritalStatus: profile.maritalStatus as string,
      switchCount:
        firstNumber(
          profile.switchCount,
          entry.switchCount,
          profile.totalNotifications,
          entry.totalNotifications,
          toPlainObject(profile.notifications).totalNotifications,
          toPlainObject(entry.notifications).totalNotifications,
        ) || 0,
      bureauId: profile.bureauId as string,
      bureauName: profile.bureauName as string,
      visibleToAll: profile.visibleToAll as string,
      citizenship: profile.citizenship,
      status: profile.status as string,
      district: firstNonEmptyString(profile.district, entry.district),
      state: firstNonEmptyString(profile.state, entry.state),
      country: firstNonEmptyString(profile.country, entry.country),
      followUpStatus: normalizeFollowUpStatus(
        firstNonEmptyString(
          profile.followUpStatus,
          entry.followUpStatus,
          profile.followupStatus,
          entry.followupStatus,
          profile.follow_up_status,
          entry.follow_up_status,
        ),
      ),
      profession: firstNonEmptyString(profile.profession, entry.profession),
      mobileNumber: firstNonEmptyString(profile.mobileNumber, entry.mobileNumber),
      countryCode: firstNonEmptyString(profile.countryCode, entry.countryCode),
      hasAcceptedInterest: profile.hasAcceptedInterest === true,
    };
  });
};

export const useMatchEndpoint = (
  endpoint: string,
  options?: UseMatchEndpointOptions,
): UseMatchEndpointResult => {
  const transformFn = options?.transform;
  const immediate = options?.immediate ?? true;
  const pageSize = options?.pageSize ?? 20;

  const computeMatches = useCallback(
    (payload: unknown) =>
      transformFn ? transformFn(payload) : defaultTransform(payload),
    [transformFn],
  );

  const matchesRef = useRef<MatchListEntry[]>([]);
  const totalRef = useRef<number>(0);
  const loadingMoreRef = useRef<boolean>(false);

  const [matches, setMatches] = useState<MatchListEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(immediate);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [lastBatchSize, setLastBatchSize] = useState<number>(0);
  const [initialLoaded, setInitialLoaded] = useState<boolean>(false);
  const [counters, setCounters] = useState<MatchCounters | null>(null);

  const fetchPage = useCallback(
    async (pageToFetch: number, replace: boolean) => {
      if (replace) {
        setLoading(true);
      } else {
        if (loadingMoreRef.current) {
          return;
        }
        loadingMoreRef.current = true;
        setLoadingMore(true);
      }
      setError(null);

      try {
        const [basePath, existingQuery] = endpoint.split("?", 2);
        const query = new URLSearchParams(existingQuery ?? "");
        if (pageToFetch > 0) {
          query.set("page", String(pageToFetch));
        }
        if (pageSize > 0) {
          query.set("limit", String(pageSize));
        }
        const queryString = query.toString();
        const url = queryString ? `${basePath}?${queryString}` : basePath;
        const response = await getRequest(url);
        if (!response?.success) {
          const message =
            response?.message || "Unable to load matches right now.";
          if (replace) {
            matchesRef.current = [];
            setMatches([]);
            setPage(0);
            setTotal(0);
            setTotalPages(0);
            setLastBatchSize(0);
            setInitialLoaded(false);
            setError(message);
          } else {
            console.warn(
              `Unable to load additional matches from ${endpoint}: ${message}`,
            );
          }
          return;
        }
        const rawData = (response.data ?? {}) as Record<string, unknown>;
        const transformed = computeMatches(rawData);
        setLastBatchSize(transformed.length);
        const nextMatches = replace
          ? transformed
          : [...matchesRef.current, ...transformed];
        matchesRef.current = nextMatches;
        setMatches(nextMatches);
        setPage(pageToFetch);
        const paginationRaw = (rawData?.pagination ?? {}) as Record<
          string,
          unknown
        >;
        const totalFromResponse =
          typeof paginationRaw.total === "number"
            ? paginationRaw.total
            : undefined;
        const totalPagesFromResponse =
          typeof paginationRaw.totalPages === "number"
            ? paginationRaw.totalPages
            : undefined;

        const computedTotal =
          totalFromResponse ??
          (replace
            ? nextMatches.length
            : Math.max(totalRef.current, nextMatches.length));
        const computedTotalPages =
          totalPagesFromResponse ??
          (totalFromResponse !== undefined && computedTotal > 0 && pageSize > 0
            ? Math.ceil(computedTotal / pageSize)
            : 0);

        totalRef.current = computedTotal;
        setTotal(computedTotal);
        setTotalPages(computedTotalPages);
        const countersRaw = rawData?.counters;
        if (countersRaw && typeof countersRaw === "object") {
          setCounters(countersRaw as MatchCounters);
        } else if (replace) {
          setCounters(null);
        }
        setInitialLoaded(true);
        setError(null);
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : typeof err === "string"
              ? err
              : "Unable to load matches right now.";
        if (replace) {
          matchesRef.current = [];
          setMatches([]);
          setPage(0);
          setTotal(0);
          setTotalPages(0);
          setLastBatchSize(0);
          setInitialLoaded(false);
          setError(message);
          setCounters(null);
        } else {
          console.warn(
            `Unable to load additional matches from ${endpoint}: ${message}`,
          );
        }
      } finally {
        if (replace) {
          setLoading(false);
        } else {
          setLoadingMore(false);
          loadingMoreRef.current = false;
        }
      }
    },
    [computeMatches, endpoint, pageSize],
  );

  const refetch = useCallback(async () => {
    matchesRef.current = [];
    totalRef.current = 0;
    loadingMoreRef.current = false;
    setMatches([]);
    setPage(0);
    setTotal(0);
    setTotalPages(0);
    setLastBatchSize(0);
    setInitialLoaded(false);
    setCounters(null);
    await fetchPage(1, true);
  }, [fetchPage]);

  useEffect(() => {
    if (!immediate) {
      return;
    }
    refetch();
  }, [immediate, refetch, endpoint]);

  const hasMore =
    initialLoaded &&
    ((totalPages > 0 && page < totalPages) ||
      (totalPages === 0 &&
        lastBatchSize === pageSize &&
        matchesRef.current.length > 0));

  const loadMore = useCallback(async () => {
    if (!hasMore || loading || loadingMoreRef.current) {
      return;
    }
    await fetchPage(page + 1, false);
  }, [fetchPage, hasMore, loading, page]);

  return {
    matches,
    setMatches,
    loading,
    loadingMore,
    error,
    total,
    hasMore,
    refetch,
    loadMore,
    counters,
  };
};

export default useMatchEndpoint;
